// src/pages/User/components/InputSection.jsx
import React, { useRef, useState } from 'react';

const ACCEPTED_FILE_TYPES = [
  '.pdf', '.PDF', 'application/pdf',
  '.docx', '.DOCX', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.txt', '.TXT', 'text/plain',
  '.md', '.MD', 'text/markdown',
  '.json', 'application/json'
].join(',');

const InputSection = ({ 
  title, setTitle, 
  folderId, setFolderId, 
  folders, loadingFolders, 
  activeTab, setActiveTab, 
  textInput, setTextInput, 
  uploadedFiles, setUploadedFiles 
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const processFiles = (rawFiles) => {
    if (!rawFiles || rawFiles.length === 0) return;
    const newFiles = Array.from(rawFiles);
    
    setUploadedFiles((prev) => {
      const existingKeys = new Set(prev.map((f) => `${f.name}_${f.size}`));
      const nonDuplicates = newFiles.filter((f) => !existingKeys.has(`${f.name}_${f.size}`));
      
      if (nonDuplicates.length === 0 && newFiles.length > 0) {
        return prev;
      }
      return [...prev, ...nonDuplicates];
    });
  };

  const handleFileChange = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      processFiles(files);
    }
    try {
      e.target.value = '';
    } catch (_) {}
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
      processFiles(e.dataTransfer.files);
    }
  };

  const removeFile = (indexToRemove) => {
    setUploadedFiles((prev) => prev.filter((_, idx) => idx !== indexToRemove));
  };

  const clearAllFiles = () => {
    setUploadedFiles([]);
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  };

  return (
    <div className="space-y-6">
      {/* Top Row: Title & Folder */}
      <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-gray-500 uppercase tracking-wide ml-1">Title</label>
            <input 
              type="text" 
              value={title} 
              onChange={(e) => setTitle(e.target.value)} 
              maxLength={30}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 outline-none text-sm" 
              placeholder="e.g. Anatomy Notes" 
              required 
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-gray-500 uppercase tracking-wide ml-1">Folder</label>
            <select 
              value={folderId} 
              onChange={(e) => setFolderId(e.target.value)} 
              className="w-full px-4 py-3 rounded-xl border border-gray-200 outline-none text-sm bg-white"
            >
              <option value="">No Folder</option>
              {loadingFolders ? (
                <option value="" disabled>Loading...</option>
              ) : (
                folders.map((f) => <option key={f._id} value={f._id}>{f.name}</option>)
              )}
            </select>
          </div>
        </div>
      </div>

      {/* Main Content Area: Tabs & Input */}
      <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm min-h-[300px]">
        {/* Tabs */}
        <div className="bg-gray-100 p-1 rounded-xl flex gap-1 mb-6">
          <button 
            type="button" 
            onClick={() => setActiveTab('file')} 
            className={`flex-1 py-2.5 rounded-lg text-sm font-bold transition-all ${activeTab === 'file' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
          >
            Upload File
          </button>
          <button 
            type="button" 
            onClick={() => setActiveTab('text')} 
            className={`flex-1 py-2.5 rounded-lg text-sm font-bold transition-all ${activeTab === 'text' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
          >
            Paste Text
          </button>
        </div>

        {/* Content */}
        {activeTab === 'file' ? (
          <div>
            {uploadedFiles.length === 0 ? (
              <label 
                onDragOver={handleDragOver}
                onDragEnter={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`w-full h-64 border-2 border-dashed rounded-2xl flex flex-col items-center justify-center cursor-pointer transition-all group ${
                  isDragging
                    ? 'border-blue-500 bg-blue-50/80 scale-[1.01]'
                    : 'border-gray-300 bg-gray-50 hover:bg-blue-50 hover:border-blue-300'
                }`}
              >
                <input 
                  ref={fileInputRef}
                  type="file" 
                  multiple 
                  accept={ACCEPTED_FILE_TYPES} 
                  className="hidden" 
                  onChange={handleFileChange} 
                />
                <div className="w-14 h-14 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center mb-3 group-hover:scale-110 group-hover:bg-blue-600 group-hover:text-white transition-all shadow-sm">
                  <i className="fa-solid fa-cloud-arrow-up text-2xl"></i>
                </div>
                <span className="text-sm font-bold text-gray-700">
                  {isDragging ? 'Drop files here' : 'Click or drag files to upload'}
                </span>
                <p className="text-xs text-gray-400 mt-1">Supports PDF, DOCX, TXT, MD, JSON</p>
              </label>
            ) : (
              <div 
                className="space-y-4"
                onDragOver={handleDragOver}
                onDragEnter={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-gray-600 uppercase tracking-wide">
                    Attached Files ({uploadedFiles.length})
                  </span>
                  <button
                    type="button"
                    onClick={clearAllFiles}
                    className="text-xs font-semibold text-red-500 hover:text-red-700 hover:underline transition-colors"
                  >
                    Clear all
                  </button>
                </div>

                <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
                  {uploadedFiles.map((file, index) => (
                    <div
                      key={`${file.name}-${index}`}
                      className="flex items-center justify-between p-3 rounded-xl border border-gray-100 bg-gray-50/80 hover:bg-blue-50/40 hover:border-blue-100 transition-colors group"
                    >
                      <div className="flex items-center gap-3 min-w-0 pr-2">
                        <div className="w-9 h-9 rounded-lg bg-blue-100/70 text-blue-600 flex items-center justify-center flex-shrink-0">
                          <i className="fa-solid fa-file-lines text-base"></i>
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-800 truncate" title={file.name}>
                            {file.name}
                          </p>
                          <p className="text-xs text-gray-400">
                            {formatFileSize(file.size)}
                          </p>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        title="Remove file"
                        className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors flex-shrink-0"
                      >
                        <i className="fa-solid fa-xmark text-base"></i>
                      </button>
                    </div>
                  ))}
                </div>

                {/* Add More Files Button */}
                <label className={`flex items-center justify-center gap-2 py-3 border-2 border-dashed rounded-xl cursor-pointer text-xs font-bold text-blue-600 transition-all ${
                  isDragging 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-gray-200 bg-gray-50 hover:bg-blue-50 hover:border-blue-300'
                }`}>
                  <input
                    type="file"
                    multiple
                    accept={ACCEPTED_FILE_TYPES}
                    className="hidden"
                    onChange={handleFileChange}
                  />
                  <i className="fa-solid fa-plus"></i>
                  <span>Add more files</span>
                </label>
              </div>
            )}
          </div>
        ) : (
          <textarea 
            value={textInput} 
            onChange={(e) => setTextInput(e.target.value)} 
            className="w-full h-80 p-5 rounded-2xl border border-gray-200 outline-none text-sm resize-none focus:border-blue-300 transition-colors" 
            placeholder="Paste your notes, articles, or raw text here..."
          ></textarea>
        )}
      </div>
    </div>
  );
};

export default InputSection;
